import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    MessageCircle,
    Loader2,
    Copy,
    Check,
    Link2,
    Unlink,
    Send,
    Phone,
    ChevronRight,
    ChevronDown,
    ExternalLink,
    Settings2,
    ShieldCheck,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { api, fmtMoney, fmtDate } from '../lib/api';

function formatCountdown(seconds) {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
}

/**
 * WhatsApp settings panel (Phase 12).
 * Lets the workspace owner:
 *   - See Twilio sandbox status
 *   - Link/unlink their own WhatsApp phone number
 *   - Copy the webhook URL to paste into Twilio sandbox settings
 *   - Send a hello message to verify the connection
 */
export function WhatsappSettingsPanel() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [phoneInput, setPhoneInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [copied, setCopied] = useState(null);
    // OTP flow state
    const [otpPending, setOtpPending] = useState(null); // { phone, expires_in, resend_in, attempts_used, attempts_max }
    const [otpCode, setOtpCode] = useState('');
    const [otpBusy, setOtpBusy] = useState(false);
    // Collapsible setup section — expanded when setup incomplete, collapsed when ready
    const [setupOpen, setSetupOpen] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getWhatsappStatus();
            setStatus(data);
            setPhoneInput(data.linked_phone || '');
            // Also check pending OTP
            const ot = await api.getWhatsappOtpStatus();
            setOtpPending(ot?.pending ? ot : null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load WhatsApp status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Auto-collapse setup steps once fully verified
    useEffect(() => {
        if (status?.configured && status?.linked_phone && status?.linked_verified) {
            setSetupOpen(false);
        } else {
            setSetupOpen(true);
        }
    }, [status?.configured, status?.linked_phone, status?.linked_verified]);

    // Tick countdowns on the pending OTP each second
    useEffect(() => {
        if (!otpPending) return undefined;
        const t = setInterval(() => {
            setOtpPending((prev) => {
                if (!prev) return prev;
                const expires_in = Math.max(0, (prev.expires_in ?? 0) - 1);
                const resend_in = Math.max(0, (prev.resend_in ?? 0) - 1);
                if (expires_in <= 0) {
                    // let user request a fresh one
                    return null;
                }
                return { ...prev, expires_in, resend_in };
            });
        }, 1000);
        return () => clearInterval(t);
    }, [otpPending]);

    const sendOtp = async () => {
        if (!phoneInput.trim()) {
            toast.error('Enter a phone number first');
            return;
        }
        setOtpBusy(true);
        try {
            const res = await api.linkWhatsappPhone(phoneInput.trim());
            toast.success('OTP sent to your WhatsApp', {
                description: `Check ${res.phone} — code expires in 5 minutes`,
            });
            setOtpPending({
                phone: res.phone,
                expires_in: res.expires_in,
                resend_in: res.resend_cooldown,
                attempts_used: 0,
                attempts_max: 5,
            });
            setOtpCode('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to send OTP');
        } finally {
            setOtpBusy(false);
        }
    };

    const verifyOtp = async () => {
        if (!/^\d{6}$/.test(otpCode.trim())) {
            toast.error('Please enter the 6-digit code');
            return;
        }
        setOtpBusy(true);
        try {
            const res = await api.verifyWhatsappOtp(otpCode.trim());
            toast.success(`Verified ${res.phone}`);
            setOtpPending(null);
            setOtpCode('');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Verification failed');
            // Refresh pending status to show updated attempt count
            try {
                const ot = await api.getWhatsappOtpStatus();
                setOtpPending(ot?.pending ? ot : null);
            } catch { /* ignore */ }
        } finally {
            setOtpBusy(false);
        }
    };

    const cancelOtp = async () => {
        setOtpBusy(true);
        try {
            await api.cancelWhatsappOtp();
            setOtpPending(null);
            setOtpCode('');
        } catch (e) {
            /* ignore */
        } finally {
            setOtpBusy(false);
        }
    };

    const unlinkPhone = async () => {
        setSaving(true);
        try {
            await api.unlinkWhatsappPhone();
            toast.success('Phone unlinked');
            setOtpPending(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to unlink phone');
        } finally {
            setSaving(false);
        }
    };

    const copy = (text, key) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(key);
            toast.success('Copied to clipboard');
            setTimeout(() => setCopied(null), 1500);
        });
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const res = await api.whatsappTestSend();
            toast.success('Hello message queued', {
                description: `Twilio SID: ${res?.twilio?.sid || 'unknown'}`,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to send test message');
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <Card className="p-5 border-border/80" data-testid="whatsapp-settings-panel">
                <div className="flex items-center gap-2 mb-4">
                    <MessageCircle size={18} className="text-primary" />
                    <div className="font-heading text-lg">WhatsApp</div>
                </div>
                <Skeleton className="h-24 w-full" />
            </Card>
        );
    }

    const configured = status?.configured;
    const linked = !!status?.linked_phone;
    const verified = !!status?.linked_verified;
    const isReady = configured && linked && verified;
    const sandboxNumber = (status?.sandbox_from || '').replace('whatsapp:', '');
    const webhookUrl = status?.webhook_url;
    return (
        <Card className="p-5 border-border/80 space-y-5" data-testid="whatsapp-settings-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-md bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                        <MessageCircle size={18} className="text-emerald-700 dark:text-emerald-400" />
                    </div>
                    <div>
                        <div className="font-heading text-lg">WhatsApp Bill Upload</div>
                        <div className="text-xs text-muted-foreground">
                            Send bill photos & PDFs directly on WhatsApp — they appear here automatically.
                        </div>
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={
                        configured
                            ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800'
                            : ''
                    }
                    data-testid="whatsapp-status-badge"
                >
                    {configured ? 'Sandbox connected' : 'Not configured'}
                </Badge>
            </div>

            {!configured && (
                <Alert variant="destructive">
                    <AlertTitle>Twilio not configured</AlertTitle>
                    <AlertDescription>
                        The backend is missing TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN. Add them to
                        backend/.env and restart.
                    </AlertDescription>
                </Alert>
            )}

            {configured && (
                <>
                    {/* Ready summary card — shown once fully verified */}
                    {isReady && (
                        <div
                            className="rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 p-4"
                            data-testid="whatsapp-ready-card"
                        >
                            <div className="flex items-start gap-3">
                                <div className="h-9 w-9 rounded-md bg-white/60 dark:bg-emerald-900 flex items-center justify-center shrink-0">
                                    <ShieldCheck className="text-emerald-700 dark:text-emerald-400" size={20} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-emerald-900 dark:text-emerald-100">
                                        Connected &amp; verified
                                    </div>
                                    <div className="text-sm text-emerald-800 dark:text-emerald-300 mt-0.5">
                                        Send any bill photo or PDF to{' '}
                                        <span className="font-mono font-medium">
                                            {sandboxNumber || '+1 415 523 8886'}
                                        </span>{' '}
                                        from{' '}
                                        <span className="font-mono font-medium">{status.linked_phone}</span>{' '}
                                        — it'll appear in your Bills list automatically.
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <Button
                                            size="sm"
                                            className="gap-2"
                                            onClick={sendTest}
                                            disabled={testing}
                                            data-testid="wa-test-send-btn"
                                        >
                                            {testing ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <Send size={14} />
                                            )}
                                            Send hello to WhatsApp
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-2"
                                            onClick={unlinkPhone}
                                            disabled={saving}
                                            data-testid="wa-unlink-btn-inline"
                                        >
                                            {saving ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <Unlink size={14} />
                                            )}
                                            Unlink number
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={() => setSetupOpen((v) => !v)}
                                            className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-800 dark:text-emerald-300 underline decoration-dotted"
                                            data-testid="wa-toggle-setup-btn"
                                        >
                                            <Settings2 size={12} />
                                            {setupOpen ? 'Hide setup steps' : 'Show setup steps'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Setup steps (1, 2, 4) — always visible while not ready; collapsible once ready */}
                    {(!isReady || setupOpen) && (
                        <div className="space-y-4" data-testid="wa-setup-steps-block">
                            {isReady && (
                                <div className="text-xs uppercase tracking-wide text-muted-foreground pt-1 pl-1">
                                    Setup reference
                                </div>
                            )}
                    {/* Step 1: Join Twilio sandbox */}
                    <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
                        <div className="flex items-start gap-3">
                            <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
                                1
                            </div>
                            <div className="space-y-2 min-w-0 flex-1">
                                <div className="font-medium">Join the sandbox from your WhatsApp (one time)</div>
                                <div className="text-sm text-muted-foreground">
                                    Open WhatsApp on your phone and send the{' '}
                                    <span className="font-mono text-foreground">join &lt;your-code&gt;</span>{' '}
                                    message shown in your{' '}
                                    <a
                                        href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline decoration-dotted inline-flex items-center gap-1"
                                    >
                                        Twilio Sandbox settings <ExternalLink size={11} />
                                    </a>{' '}
                                    to this number:
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <code
                                        className="rounded-md bg-background border border-border/60 px-2.5 py-1.5 text-sm font-mono"
                                        data-testid="wa-sandbox-number"
                                    >
                                        {sandboxNumber || '+1 415 523 8886'}
                                    </code>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1"
                                        onClick={() => copy(sandboxNumber, 'sandbox')}
                                        data-testid="wa-copy-sandbox-btn"
                                    >
                                        {copied === 'sandbox' ? <Check size={14} /> : <Copy size={14} />}
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 2: Set webhook URL in Twilio */}
                    <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
                        <div className="flex items-start gap-3">
                            <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
                                2
                            </div>
                            <div className="space-y-2 min-w-0 flex-1">
                                <div className="font-medium">
                                    Paste this webhook URL into Twilio Sandbox settings
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    In the Twilio console → Messaging → Try it out → WhatsApp sandbox
                                    settings, set{' '}
                                    <span className="font-medium">"WHEN A MESSAGE COMES IN"</span> to
                                    this URL (HTTP POST):
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <code
                                        className="rounded-md bg-background border border-border/60 px-2.5 py-1.5 text-xs font-mono break-all flex-1 min-w-0"
                                        data-testid="wa-webhook-url"
                                    >
                                        {webhookUrl || 'Set PUBLIC_BACKEND_URL in backend/.env'}
                                    </code>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1 shrink-0"
                                        disabled={!webhookUrl}
                                        onClick={() => copy(webhookUrl, 'webhook')}
                                        data-testid="wa-copy-webhook-btn"
                                    >
                                        {copied === 'webhook' ? <Check size={14} /> : <Copy size={14} />}
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                        </div>
                    )}

                    {/* Step 3: Link phone (with OTP) — hidden once verified */}
                    {!isReady && (
                    <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
                        <div className="flex items-start gap-3">
                            <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
                                3
                            </div>
                            <div className="space-y-2 min-w-0 flex-1">
                                <div className="font-medium">Verify your WhatsApp number</div>
                                <div className="text-sm text-muted-foreground">
                                    We'll send a 6-digit code to your WhatsApp. Enter it here to prove
                                    you own the number — this prevents anyone else from linking your phone.
                                </div>

                                {linked && !otpPending && (
                                    <div className="rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300 flex items-center justify-between gap-2 flex-wrap">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Check size={14} />
                                            Verified{' '}
                                            <span className="font-mono">{status.linked_phone}</span>
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-2"
                                            onClick={unlinkPhone}
                                            disabled={saving}
                                            data-testid="wa-unlink-btn"
                                        >
                                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />}
                                            Unlink
                                        </Button>
                                    </div>
                                )}

                                {!linked && !otpPending && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="flex-1 min-w-[220px]">
                                            <Label htmlFor="wa-phone" className="sr-only">
                                                WhatsApp number
                                            </Label>
                                            <div className="relative">
                                                <Phone
                                                    size={14}
                                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                                />
                                                <Input
                                                    id="wa-phone"
                                                    data-testid="wa-phone-input"
                                                    value={phoneInput}
                                                    onChange={(e) => setPhoneInput(e.target.value)}
                                                    placeholder="+91 98XXXXXXXX"
                                                    className="pl-9 font-mono"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            className="gap-2"
                                            onClick={sendOtp}
                                            disabled={otpBusy || !phoneInput.trim()}
                                            data-testid="wa-send-otp-btn"
                                        >
                                            {otpBusy ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <Send size={14} />
                                            )}
                                            Send OTP
                                        </Button>
                                    </div>
                                )}

                                {otpPending && (
                                    <div
                                        className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2.5"
                                        data-testid="wa-otp-form"
                                    >
                                        <div className="text-sm">
                                            OTP sent to{' '}
                                            <span className="font-mono font-medium">
                                                {otpPending.phone}
                                            </span>
                                            . Check your WhatsApp — the code expires in{' '}
                                            <span
                                                className="font-mono tabular-nums"
                                                data-testid="wa-otp-countdown"
                                            >
                                                {formatCountdown(otpPending.expires_in)}
                                            </span>
                                            .
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Input
                                                data-testid="wa-otp-input"
                                                value={otpCode}
                                                onChange={(e) =>
                                                    setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                                                }
                                                placeholder="6-digit code"
                                                inputMode="numeric"
                                                maxLength={6}
                                                className="font-mono tracking-[0.4em] text-center flex-1 min-w-[140px]"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') verifyOtp();
                                                }}
                                            />
                                            <Button
                                                onClick={verifyOtp}
                                                disabled={otpBusy || otpCode.length !== 6}
                                                data-testid="wa-verify-otp-btn"
                                                className="gap-2"
                                            >
                                                {otpBusy ? (
                                                    <Loader2 size={14} className="animate-spin" />
                                                ) : (
                                                    <Check size={14} />
                                                )}
                                                Verify
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                            <button
                                                type="button"
                                                onClick={sendOtp}
                                                disabled={otpBusy || otpPending.resend_in > 0}
                                                className="underline decoration-dotted disabled:no-underline disabled:opacity-60"
                                                data-testid="wa-resend-otp-btn"
                                            >
                                                {otpPending.resend_in > 0
                                                    ? `Resend in ${otpPending.resend_in}s`
                                                    : 'Resend code'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={cancelOtp}
                                                disabled={otpBusy}
                                                className="underline decoration-dotted"
                                                data-testid="wa-cancel-otp-btn"
                                            >
                                                Cancel
                                            </button>
                                            {(otpPending.attempts_used || 0) > 0 && (
                                                <span>
                                                    {otpPending.attempts_used} / {otpPending.attempts_max}{' '}
                                                    attempts used
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!linked && !otpPending && (
                                    <div className="text-xs text-muted-foreground">
                                        Note: this number must have already joined the Twilio sandbox
                                        (Step 1). If it hasn't, we can't deliver the code.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Step 4: Send test hello — only shown while setup is expanded / incomplete */}
                    {(!isReady || setupOpen) && (
                    <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
                        <div className="flex items-start gap-3">
                            <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
                                4
                            </div>
                            <div className="space-y-2 min-w-0 flex-1">
                                <div className="font-medium">Verify — say hi</div>
                                <div className="text-sm text-muted-foreground">
                                    We'll send a "Hello from Ease My Bill" message to your linked number.
                                    Then reply on WhatsApp with any bill photo or PDF.
                                </div>
                                <Button
                                    onClick={sendTest}
                                    disabled={!linked || testing}
                                    className="gap-2"
                                    data-testid="wa-test-send-btn-step4"
                                >
                                    {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                    Send hello to WhatsApp
                                </Button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Recent WhatsApp bills */}
                    {status?.recent_whatsapp_bills?.length > 0 && (
                        <div>
                            <div className="text-sm font-medium mb-2">Recent WhatsApp bills</div>
                            <div className="space-y-1.5" data-testid="wa-recent-list">
                                {status.recent_whatsapp_bills.map((b) => (
                                    <a
                                        key={b.id}
                                        href={`/bills/${b.id}`}
                                        className="flex items-center gap-2 rounded-md border border-border/60 bg-background/60 px-3 py-2 text-sm hover:bg-muted transition-colors"
                                    >
                                        <MessageCircle size={14} className="text-emerald-600 shrink-0" />
                                        <div className="min-w-0 flex-1 truncate">
                                            <span className="font-medium">
                                                {b.vendor || 'Unknown vendor'}
                                            </span>
                                            {b.bill_date && (
                                                <span className="text-muted-foreground text-xs">
                                                    {' · '}
                                                    {fmtDate(b.bill_date)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-mono text-xs shrink-0">
                                            {fmtMoney(b.total, b.currency)}
                                        </div>
                                        <ChevronRight size={14} className="text-muted-foreground shrink-0" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
